// Occupancy tiers — a room can have multiple prices by guest count.
// Dynamic: hotels fill only the tiers they use; empty tiers are ignored everywhere.
export const OCCUPANCY_TIERS = ["single", "double", "triple", "quad"] as const;
export type Occupancy = (typeof OCCUPANCY_TIERS)[number];

export const OCC_LABEL: Record<Occupancy, string> = {
  single: "Single",
  double: "Double",
  triple: "Triple",
  quad: "Quad",
};
export const OCC_SHORT: Record<Occupancy, string> = {
  single: "Sgl",
  double: "Dbl",
  triple: "Tpl",
  quad: "Quad",
};

// A per-occupancy price entry (e.g. type_avg_prices[type]). Legacy value may be a plain number = double.
export type OccRates = { single?: number | ""; double?: number | ""; triple?: number | ""; quad?: number | "" };

// Pick a per-occupancy value from a season rate cell OR a type_avg entry (number|object), falling back to double.
export function pickOcc(entry: any, occ: Occupancy): number {
  if (entry == null) return 0;
  if (typeof entry === "number") return occ === "double" ? entry : 0;
  if (typeof entry === "object") return Number(entry[occ] ?? entry.double ?? 0) || 0;
  return Number(entry) || 0;
}

// Normalize a stored entry (number | object) to an OccRates object of strings for form editing.
export function toOccForm(entry: any): Record<Occupancy, string> {
  const out: Record<Occupancy, string> = { single: "", double: "", triple: "", quad: "" };
  if (entry == null) return out;
  if (typeof entry === "number") { out.double = entry > 0 ? String(entry) : ""; return out; }
  if (typeof entry === "object") {
    for (const o of OCCUPANCY_TIERS) {
      const v = entry[o];
      out[o] = v != null && v !== "" && Number(v) > 0 ? String(v) : "";
    }
  }
  return out;
}

// Build the stored object from an OccRates form (omit empty tiers). Returns undefined if all empty.
export function fromOccForm(form: Record<Occupancy, string>): OccRates | undefined {
  const out: OccRates = {};
  let any = false;
  for (const o of OCCUPANCY_TIERS) {
    const v = form[o];
    if (v !== "" && v !== undefined && !isNaN(Number(v))) { out[o] = Number(v); any = true; }
  }
  return any ? out : undefined;
}
